import './appointment-target-form.ajscss';

import { IAgentConfigDeprecatedService } from '@agent/core/agent/agent-config-deprecated.service';
import { IContactDeprecatedService } from '@agent/core/contact/contact-deprecated.service';
import { ITargetSelection, ITargetSelectionCsr } from '@agent/core/contact/transfer/models/transfer';
import { ISearchService } from '@agent/core/search/search.service';
import { SessionDeprecatedService } from '@commons/core/auth/session-deprecated.service';
import { IComponentController, IComponentOptions } from 'angular';

import { ChannelType, Group, Option, ProfileFacade, Skill, Team, untilDestroyed } from '@contact/upgrade';

import templateUrl from './appointment-target-form.ajs.html';
import { BehaviorSubject, debounceTime } from 'rxjs';

const MODULE_NAME = 'appointmentTargetForm';
export type CsrFilter = { csrPrefix?: string; teamId?: string; groupId?: string; skillIds?: string[] };

class AppointmentTargetFormCtrl implements IComponentController {
  public name = MODULE_NAME;
  public ChannelType = ChannelType;

  // binding
  public availableSkills: any[];
  public onlySkills: boolean;
  public eventTargetChange: Function;
  public htmlPrefix: string;
  public mode: string = 'PHONE';

  // internal properties
  public selectedTarget: ITargetSelection;
  public skills: Skill[];
  public teams: Team[];
  public groups: Group[];
  public csrs: ITargetSelectionCsr[];
  public originalTeams: Team[];
  public originalGroups: Group[];
  public selectedSkills: Skill[];
  public selectedTeam: Team;
  public selectedGroup: Group;
  public selectedCsr: ITargetSelectionCsr;
  public noSelectedCsr: ITargetSelectionCsr;
  public skillIsValid: boolean;

  _visibleDomainUuids: string[] = [];

  protected readonly _agentChosen$ = new BehaviorSubject<string>('');
  readonly agentChosen$ = this._agentChosen$.asObservable();

  constructor(
    private readonly agentConfigDeprecatedService: IAgentConfigDeprecatedService,
    private readonly search: ISearchService,
    public readonly contactDeprecatedService: IContactDeprecatedService,
    private readonly sessionDeprecatedService: SessionDeprecatedService,
    protected readonly profileFacade: ProfileFacade
  ) {}

  $onInit(): void {
    this.selectedTarget = {};
    this.skills = [];
    this.teams = [];
    this.groups = [];
    this.csrs = [];
    this.originalTeams = [];
    this.originalGroups = [];
    this.selectedSkills = [];
    this.skillIsValid = false;
    // skill available
    this.skills = [...this.availableSkills].sort((a, b) => this.sortUpperCase(a, b));

    // domain teams available
    this.agentConfigDeprecatedService.domainTeams.forEach(team => this.teams.push(team));
    // domain grop available
    this.agentConfigDeprecatedService.domainGroups.forEach(group => this.groups.push(group));

    this._agentChosen$.pipe(debounceTime(500)).subscribe(csrPrefix => this.updateCandicateCsr(csrPrefix));
    this.profileFacade.profile$.pipe(untilDestroyed(this)).subscribe(profile => (this._visibleDomainUuids = profile.accessibleDomainUuids));
  }

  $onDestroy(): void {
    this._agentChosen$.complete();
  }

  private sortUpperCase(a, b): number {
    const nameA = a.name.toUpperCase();
    const nameB = b.name.toUpperCase();
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    return 0;
  }

  getAgentAutocompleteText(): string {
    let input: HTMLInputElement = document.getElementById(this.htmlPrefix + '_agentSelection') as HTMLInputElement;
    if (input) return input.value || '';
    return '';
  }

  onCsrChange(): void {
    this._agentChosen$.next(this.getAgentAutocompleteText());
  }

  updateCandicateCsr(csrPrefix: string = ''): void {
    let teamId = this.selectedTeam ? this.selectedTeam.id : null;
    let groupId: string | null = this.selectedGroup ? this.selectedGroup.id : null;
    let skillIds = this.selectedSkills.map(skill => skill.id);

    this.skillIsValid = !!this.selectedSkills && this.selectedSkills.length > 0;

    let skills = Option.of(skillIds).getOrElse([]);
    let channel = this.mode == 'PHONE' ? 'PHONE_OUTGOING' : 'VISIO_OUTGOING';

    // reset the csr
    this.selectedCsr = this.noSelectedCsr;
    this.search
      .searchCsr(teamId || '', groupId || '', skills, csrPrefix, channel, true, this._visibleDomainUuids)
      .then((csrList: any) => {
        let candidateCsrs: ITargetSelectionCsr[] = csrList.map(c => ({
          name: c.lastName + ' ' + c.firstName,
          email: c.mail,
          uuid: c.uuid,
          login: c.login,
          firstName: c.firstName,
          lastName: c.lastName,
        }));
        this.csrs = [...candidateCsrs].sort((a, b) => this.sortUpperCase(a, b));
      })
      .finally(() => {
        this.onTargetChange();
      });
  }

  onCsrTeamChange(): void {
    let input: HTMLInputElement = document.getElementById('popupCsrTeamSelection') as HTMLInputElement;
    let teamVal = '';
    if (input) teamVal = input.value || '';
    this.teams = this.originalTeams.filter(_team => _team.name.indexOf(teamVal) >= 0);
  }

  onCsrGroupChange(): void {
    let input: HTMLInputElement = document.getElementById('popupCsrGroupSelection') as HTMLInputElement;
    let groupVal = '';
    if (input) groupVal = input.value || '';
    this.groups = this.originalGroups.filter(_group => _group.name.indexOf(groupVal) >= 0);
  }

  onSkillSelected(): void {
    this.updateCandicateCsr();
  }

  onTargetChange(): void {
    let targetApp: ITargetSelection = { csr: this.selectedCsr, skills: this.selectedSkills };
    if (!!this.selectedTeam && !!this.selectedTeam.id) {
      targetApp.teams = [this.selectedTeam];
    }
    if (!!this.selectedGroup && !!this.selectedGroup.id) {
      targetApp.groups = [this.selectedGroup];
    }
    this.eventTargetChange({ $event: { target: targetApp } });
  }
}

export class AppointmentTargetForm implements IComponentOptions {
  templateUrl = templateUrl;
  bindings = {
    availableSkills: '<',
    onlySkills: '@',
    htmlPrefix: '@',
    eventTargetChange: '&',
  } as { [binding: string]: string };
  controller = [
    'agentConfigDeprecatedService',
    'search',
    'contactDeprecatedService',
    'sessionDeprecatedService',
    'profileFacade',
    AppointmentTargetFormCtrl,
  ];
}

export default MODULE_NAME;
